"use client";

import type { ReactNode } from "react";
import { HoverPrefetchLink } from "@/components/ui/hover-prefetch-link";
import { ArrowRight } from "@/components/icons";
import { Button } from "@/components/ui/button";
import type { NavLinkGroup, NavLinkItem, NavMenu } from "./types";

function NavLinkButton({
  link,
  isActive,
  onLinkSelect,
  variant = "quiet",
  children,
}: {
  link: NavLinkItem;
  isActive: (href: string) => boolean;
  onLinkSelect: () => void;
  variant?: "quiet" | "link";
  children?: ReactNode;
}) {
  return (
    <Button
      asChild
      variant={variant}
      onClick={onLinkSelect}
      className="w-full justify-start pointer-coarse:min-h-11"
    >
      <HoverPrefetchLink
        href={link.href}
        aria-label={link.ariaLabel}
        aria-current={isActive(link.href) ? "page" : undefined}
      >
        {link.label}
        {children}
      </HoverPrefetchLink>
    </Button>
  );
}

function LinkList({
  links,
  isActive,
  onLinkSelect,
}: {
  links: NavLinkItem[];
  isActive: (href: string) => boolean;
  onLinkSelect: () => void;
}) {
  return (
    <ul className="flex flex-col">
      {links.map((link) => (
        <li key={link.href}>
          <NavLinkButton link={link} isActive={isActive} onLinkSelect={onLinkSelect} />
        </li>
      ))}
    </ul>
  );
}

function LinkGroups({
  groups,
  layout,
  isActive,
  onLinkSelect,
}: {
  groups: NavLinkGroup[];
  layout: "columns" | "stack";
  isActive: (href: string) => boolean;
  onLinkSelect: () => void;
}) {
  return (
    <div className={layout === "columns" ? "grid grid-cols-5 gap-x-2" : "flex flex-col gap-3"}>
      {groups.map((group) => (
        <section key={group.heading} aria-label={group.heading}>
          <h3 className="px-4 pb-1 pt-2 text-data font-medium text-muted-foreground">{group.heading}</h3>
          <LinkList links={group.links} isActive={isActive} onLinkSelect={onLinkSelect} />
        </section>
      ))}
    </div>
  );
}

export function NavMenuLinks({
  menu,
  isActive,
  onLinkSelect,
  layout = "stack",
}: {
  menu: NavMenu;
  isActive: (href: string) => boolean;
  onLinkSelect: () => void;
  layout?: "columns" | "stack";
}) {
  return (
    <div className="flex flex-col">
      {menu.hub && (
        <div className="mb-1 border-b border-border pb-1">
          <NavLinkButton
            link={menu.hub}
            isActive={isActive}
            onLinkSelect={onLinkSelect}
            variant="link"
          >
            <ArrowRight className="size-4" aria-hidden />
          </NavLinkButton>
        </div>
      )}
      {menu.groups ? (
        <LinkGroups groups={menu.groups} layout={layout} isActive={isActive} onLinkSelect={onLinkSelect} />
      ) : (
        <LinkList links={menu.links} isActive={isActive} onLinkSelect={onLinkSelect} />
      )}
    </div>
  );
}
